/**
 * vrs_join_marks: the room page's own critical path, one event per room load.
 *
 * The join stage events (vrs_signaling_connected and the rest) are timed with
 * Date.now() and read on the server by receipt time, so the 2026-09-24
 * investigation could only infer where an interpreter's 2-3 s between page
 * boot and joining went, and "first remote video" meant the remote track's
 * arrival, not a frame on screen. This module keeps one set of marks, all on
 * the page's performance clock (ms since performance.timeOrigin, which is
 * where the navigation began), and reports them once:
 *
 * - after the first frame of the other side's video is on screen, or
 * - when the page leaves the call or unloads first (partial: true).
 *
 * Marks come from the app (App and Conference mount, device setup, the
 * conference init, connect(), the initial camera/microphone request, the
 * camera grace, the other side entering the room) and from lib-jitsi-meet's
 * own timestamps (Strophe connecting/connected, MUC join, session-initiate,
 * ICE). The lib keeps only the latest time per key, so those are captured at
 * the join's milestones, first value kept (captureJoinLibTimes), and no longer
 * read once the media connection has settled (LIB_TIMES_SETTLE_MS).
 * Only the whitelisted names below leave the page, and only as finite
 * numbers; nothing else about the call, the devices or the people is sent.
 *
 * A room page a portal prerendered (shared/room-prerender.js) starts its clock
 * when the prerender starts, before anyone accepted anything. Its marks leave
 * the page measured from when it was shown (activationStart), so they mean the
 * same on every page; what happened before that is summed up as
 * preActivationStage. The event also says how the page was entered (entryMode,
 * the handoff outcome, gate violations) and the device's memory class.
 */
import { describePreActivationStage, getRoomEntry } from '../app/roomActivation.web';
import { findBundleTiming } from '../interpreter-queue/joinTimeline';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
import { getActiveTransport } from '../visual-language-media-policy/p2pTransport';
export const JOIN_MARKS_EVENT = 'vrs_join_marks';
/** Written by index.html immediately after the blocking app bundle returns. */
export const APP_BUNDLE_EVALUATED_MARK = 'vrs-app-bundle-evaluated';
/** Every numeric mark the event may carry, in critical-path order. */
export const JOIN_MARK_NAMES = [
    'bundleStart',
    'bundleWorkerStart',
    'bundleRequestStart',
    'bundleResponseStart',
    'bundleResponseEnd',
    'appBoot',
    'appBundleEvaluated',
    'domContentLoaded',
    'appMount',
    'conferenceMount',
    'setupDevicesStart',
    'setupDevicesEnd',
    'initStart',
    'connectDispatched',
    'gumStart',
    'gumEnd',
    'xmppConnecting',
    'xmppConnected',
    'mucJoinStarted',
    'mucJoined',
    'remoteParticipantJoined',
    'sessionInitiate',
    'iceChecking',
    'iceConnected',
    'firstRemoteTrack',
    'firstRemoteFrame'
];
const GRACE_OUTCOMES = ['withTracks', 'expired', 'lateAttach'];
/** lib-jitsi-meet JitsiConnection.getConnectionTimes(): Strophe status names. */
const XMPP_TIME_MARKS = {
    connected: 'xmppConnected',
    connecting: 'xmppConnecting'
};
/** lib-jitsi-meet JitsiConference.getConnectionTimes() for the JVB session. */
const CONFERENCE_TIME_MARKS = {
    'ice.state.checking': 'iceChecking',
    'ice.state.connected': 'iceConnected',
    'muc.join.started': 'mucJoinStarted',
    'muc.joined': 'mucJoined',
    'session.initiate': 'sessionInitiate'
};
/** A mark later than this (a day) is not a time on this page's clock. */
const MAX_MARK_MS = 24 * 60 * 60 * 1000;
/**
 * lib-jitsi-meet keeps the latest time per key: a reconnect rewrites the
 * Strophe times, a JVB session restart session.initiate, and every ICE state
 * change its ice.state key. This long after the media connection was first
 * established the join has settled, and the lib's times are no longer read:
 * a partial event sent at hangup an hour later reports the join, not the last
 * Wi-Fi blip. Until then, the first value read for each key is kept.
 */
export const LIB_TIMES_SETTLE_MS = 30000;
/**
 * A hidden video element never presents a frame, so its frame callback may
 * never run; its loadeddata time stands in after this long.
 */
export const FRAME_CALLBACK_FALLBACK_MS = 1000;
/** A join page never has this many long tasks; past it, the rest are not kept. */
const MAX_LONG_TASKS = 500;
let session;
function now() {
    const perf = globalThis.performance;
    return typeof perf?.now === 'function' ? perf.now() : undefined;
}
function isMarkTime(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= MAX_MARK_MS;
}
function readTimes(source) {
    try {
        const times = source?.getConnectionTimes?.();
        return times && typeof times === 'object' ? times : {};
    }
    catch {
        return {};
    }
}
/**
 * Copies the valid times of the given keys that were not read before.
 *
 * @param {Object} into - The first value read for each key.
 * @param {Object} times - The lib's times now.
 * @param {string[]} keys - The keys the marks use.
 * @returns {void}
 */
function keepFirst(into, times, keys) {
    keys.forEach(key => {
        const value = times[key];
        if (into[key] === undefined && isMarkTime(value)) {
            into[key] = value;
        }
    });
}
function readBundleTiming() {
    try {
        const perf = globalThis.performance;
        const entries = perf?.getEntriesByType?.('resource');
        return Array.isArray(entries) ? findBundleTiming(entries) : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Reads the browser-owned boundaries around bundle evaluation and DOM ready.
 * They are read when the event is sent, after DOMContentLoaded has happened;
 * the earlier room-load breadcrumb runs during bundle evaluation, when the
 * Navigation Timing value is still zero.
 */
function readBrowserLoadMarks() {
    try {
        const perf = globalThis.performance;
        const navigationEntries = perf?.getEntriesByType?.('navigation');
        const navigation = Array.isArray(navigationEntries) ? navigationEntries[0] : undefined;
        const evaluatedEntries = perf?.getEntriesByName?.(APP_BUNDLE_EVALUATED_MARK, 'mark');
        const evaluated = Array.isArray(evaluatedEntries)
            ? evaluatedEntries[0] : undefined;
        return {
            appBundleEvaluated: evaluated?.startTime,
            domContentLoaded: navigation?.domContentLoadedEventStart
        };
    }
    catch {
        return {};
    }
}
/**
 * The marks as they may leave the page: whitelisted names with finite,
 * non-negative millisecond values (rounded), plus a known grace outcome.
 * lib-jitsi-meet's timestamps fill in their marks; a mark the app recorded
 * itself is kept. Everything else is dropped.
 *
 * @param {Object} input - Collected marks and the lib's own timestamps.
 * @returns {JoinMarks}
 */
export function buildJoinMarks({ activationStart = 0, bundle, conferenceTimes = {}, graceOutcome, marks = {}, xmppTimes = {} }) {
    const collected = {
        bundleRequestStart: bundle?.requestStart,
        bundleResponseEnd: bundle?.responseEnd,
        bundleResponseStart: bundle?.responseStart,
        bundleStart: bundle?.startTime,
        // 0 means no service worker handled the request.
        bundleWorkerStart: bundle?.workerStart || undefined
    };
    Object.entries(XMPP_TIME_MARKS).forEach(([key, mark]) => {
        collected[mark] = xmppTimes[key];
    });
    Object.entries(CONFERENCE_TIME_MARKS).forEach(([key, mark]) => {
        collected[mark] = conferenceTimes[key];
    });
    JOIN_MARK_NAMES.forEach(name => {
        if (isMarkTime(marks[name])) {
            collected[name] = marks[name];
        }
    });
    const result = {};
    // From when the page was shown. A mark before that (a prerendered page's
    // bundle download) is not part of anyone's wait and is left out.
    const shownAt = typeof activationStart === 'number' && Number.isFinite(activationStart) && activationStart > 0
        ? activationStart : 0;
    JOIN_MARK_NAMES.forEach(name => {
        const value = collected[name];
        if (isMarkTime(value) && isMarkTime(value - shownAt)) {
            result[name] = Math.round(value - shownAt);
        }
    });
    if (GRACE_OUTCOMES.includes(graceOutcome)) {
        result.graceOutcome = graceOutcome;
    }
    return result;
}
/**
 * Records a mark once: the first time the page reaches it. Later calls (a
 * rejoin, a breakout room) do not move it. Ignored on pages without a room.
 *
 * @param {JoinMarkName} name - The mark.
 * @param {number} [at] - The time on the performance clock; now by default.
 * @returns {void}
 */
export function markJoin(name, at = now()) {
    if (!session || session.sent || !JOIN_MARK_NAMES.includes(name) || !isMarkTime(at)
        || session.marks[name] !== undefined) {
        return;
    }
    session.marks[name] = at;
}
/**
 * How the join met the camera grace. A join that went ahead without the
 * camera becomes lateAttach once the camera is attached.
 *
 * @param {JoinGraceOutcome} outcome - The outcome.
 * @returns {void}
 */
export function setJoinGraceOutcome(outcome) {
    if (!session || session.sent || !GRACE_OUTCOMES.includes(outcome)) {
        return;
    }
    if (!session.graceOutcome || (session.graceOutcome === 'expired' && outcome === 'lateAttach')) {
        session.graceOutcome = outcome;
    }
}
/**
 * The join was started at the route decision, before the conference screen
 * rendered (conference/actions.web.ts prestartJoin).
 *
 * @returns {void}
 */
export function noteJoinPrestarted() {
    if (session && !session.sent) {
        session.prestarted = true;
    }
}
/**
 * The signaling connection, whose Strophe timestamps become xmpp marks.
 *
 * @param {Object} connection - The JitsiConnection.
 * @returns {void}
 */
export function noteJoinConnection(connection) {
    if (session && !session.connection && connection) {
        session.connection = connection;
    }
}
/**
 * Reads lib-jitsi-meet's times now, keeping the first value of each key.
 * Called at the join's milestones (signaling up, the room joined, the other
 * side arriving, its camera, the media connection, sending); a no-op once the
 * media connection has settled.
 *
 * @returns {void}
 */
export function captureJoinLibTimes() {
    if (!session || session.sent || session.libTimesClosed) {
        return;
    }
    keepFirst(session.libTimes.xmpp, readTimes(session.connection), Object.keys(XMPP_TIME_MARKS));
    keepFirst(session.libTimes.conference, readTimes(session.conference), Object.keys(CONFERENCE_TIME_MARKS));
}
/**
 * The media connection (ICE) was established: capture the lib's times, and
 * once more when the join has settled, after which they are no longer read.
 *
 * @returns {void}
 */
export function noteJoinMediaConnected() {
    const current = session;
    if (!current || current.sent || current.libTimesClosed || current.libTimesSettling) {
        return;
    }
    captureJoinLibTimes();
    current.libTimesSettling = true;
    const timer = setTimeout(() => {
        if (session === current) {
            captureJoinLibTimes();
            current.libTimesClosed = true;
        }
    }, LIB_TIMES_SETTLE_MS);
    current.stopWatching.push(() => clearTimeout(timer));
}
/**
 * The conference being joined, whose MUC, session and ICE timestamps become
 * marks. The first one counts: it is this page's join.
 *
 * @param {Object} conference - The JitsiConference.
 * @param {string} [mediaConnectedEvent] - Its event for the media connection
 * being established (JitsiConferenceEvents.CONNECTION_ESTABLISHED).
 * @returns {void}
 */
export function noteJoinConference(conference, mediaConnectedEvent) {
    if (!session || session.conference || !conference) {
        return;
    }
    session.conference = conference;
    if (mediaConnectedEvent && typeof conference.on === 'function') {
        try {
            conference.on(mediaConnectedEvent, noteJoinMediaConnected);
            session.stopWatching.push(() => conference.off?.(mediaConnectedEvent, noteJoinMediaConnected));
        }
        catch { /* the marks then read the times when they are sent */ }
    }
}
/**
 * The other side entered the room (its first remote participant). On the
 * page that joined first this is the other side arriving; on the second, it
 * is about when this page joined.
 *
 * @returns {void}
 */
export function noteRemoteParticipantJoined() {
    markJoin('remoteParticipantJoined');
    captureJoinLibTimes();
}
/**
 * The other side's video track arrived. Its first frame is watched for on
 * whichever video element shows it (see watchRemoteFrames).
 *
 * @param {string} [mediaTrackId] - The MediaStreamTrack id.
 * @param {boolean} [p2p] - The track comes over the direct connection.
 * @returns {void}
 */
export function noteRemoteVideoTrack(mediaTrackId, p2p) {
    if (!session || session.sent) {
        return;
    }
    markJoin('firstRemoteTrack');
    captureJoinLibTimes();
    if (mediaTrackId) {
        session.remoteVideoTrackIds.add(mediaTrackId);
        if (p2p === true) {
            session.remoteVideoP2PTrackIds.add(mediaTrackId);
        }
    }
}
/**
 * The first remote video frame is on screen: the marks are complete.
 *
 * @param {number} at - The time on the performance clock.
 * @returns {void}
 */
export function noteRemoteVideoFrame(at = now()) {
    if (!session || session.sent) {
        return;
    }
    markJoin('firstRemoteFrame', at);
    sendJoinMarks('firstRemoteFrame');
}
/**
 * Which transport carries the other side's video a media element shows, or
 * undefined when it shows none of theirs.
 *
 * @param {Object} target - The element that loaded data.
 * @returns {string|undefined}
 */
function remoteVideoShown(target) {
    if (!session || !target || String(target.tagName || '').toUpperCase() !== 'VIDEO') {
        return undefined;
    }
    try {
        const { remoteVideoP2PTrackIds, remoteVideoTrackIds } = session;
        const shown = target.srcObject?.getVideoTracks?.().find(track => track?.id && remoteVideoTrackIds.has(track.id));
        return shown?.id ? remoteVideoP2PTrackIds.has(shown.id) ? 'p2p' : 'jvb' : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Media events do not bubble, but a capturing listener on the document sees
 * every video element's loadeddata. When the element shows the other side,
 * its next presented frame (requestVideoFrameCallback) is the first remote
 * frame; without that API, or when the element is hidden and presents none,
 * loadeddata (the first decoded frame) stands in.
 *
 * @returns {Function} Removes the listener.
 */
function watchRemoteFrames() {
    const doc = globalThis.document;
    if (!doc || typeof doc.addEventListener !== 'function') {
        return () => undefined;
    }
    const onLoadedData = (event) => {
        const target = event.target;
        const transport = remoteVideoShown(target);
        if (!transport) {
            return;
        }
        if (session && !session.firstFrameTransport) {
            session.firstFrameTransport = transport;
        }
        const loadedAt = now();
        if (typeof target?.requestVideoFrameCallback !== 'function') {
            noteRemoteVideoFrame(loadedAt);
            return;
        }
        try {
            target.requestVideoFrameCallback(frameAt => noteRemoteVideoFrame(frameAt));
        }
        catch {
            noteRemoteVideoFrame(loadedAt);
            return;
        }
        setTimeout(() => noteRemoteVideoFrame(loadedAt), FRAME_CALLBACK_FALLBACK_MS);
    };
    doc.addEventListener('loadeddata', onLoadedData, true);
    return () => doc.removeEventListener('loadeddata', onLoadedData, true);
}
/**
 * Collects the page's long main-thread tasks (over 50 ms). The XMPP login is
 * one round trip per step, and each answer waits for a free main thread, so
 * the long tasks inside the login tell page work apart from the network.
 */
function watchLongTasks() {
    const Observer = globalThis.PerformanceObserver;
    const current = session;
    if (!current || typeof Observer !== 'function' || !Observer.supportedEntryTypes?.includes('longtask')) {
        return () => undefined;
    }
    const tasks = [];
    const keep = (entries = []) => {
        for (const entry of entries) {
            if (tasks.length < MAX_LONG_TASKS && isMarkTime(entry.startTime) && entry.duration >= 0) {
                tasks.push([entry.startTime, entry.startTime + entry.duration]);
            }
        }
    };
    let observer;
    try {
        observer = new Observer(list => keep(list.getEntries()));
        observer.observe({ buffered: true, type: 'longtask' });
    }
    catch {
        return () => undefined;
    }
    current.longTasks = tasks;
    return () => {
        try {
            keep(observer.takeRecords?.());
            observer.disconnect();
        }
        catch { /* nothing more to read */ }
    };
}
/**
 * The long-task time inside [from, to] (performance.now()), rounded.
 *
 * @param {Array} tasks - Long tasks as [start, end].
 * @param {number} from - Window start.
 * @param {number} to - Window end.
 * @returns {number|undefined} Milliseconds, or undefined without a window.
 */
export function longTaskOverlapMs(tasks, from, to) {
    if (!isMarkTime(from) || !isMarkTime(to) || to < from) {
        return undefined;
    }
    let total = 0;
    for (const [start, end] of tasks) {
        total += Math.max(0, Math.min(end, to) - Math.max(start, from));
    }
    return Math.round(total);
}
/** The browser's own estimate of the round trip (navigator.connection, Chromium), rounded to 25 ms by the browser. */
function readNetworkRtt() {
    const rtt = globalThis.navigator?.connection?.rtt;
    return typeof rtt === 'number' && Number.isFinite(rtt) && rtt >= 0 ? rtt : undefined;
}
function watchPagehide() {
    const win = globalThis.window;
    if (!win || typeof win.addEventListener !== 'function') {
        return () => undefined;
    }
    const onPagehide = () => sendJoinMarks('pagehide');
    win.addEventListener('pagehide', onPagehide);
    return () => win.removeEventListener('pagehide', onPagehide);
}
/**
 * Starts collecting marks for this room page load. Called once, by the room
 * boot (App.web.tsx), only on pages with a conference.
 *
 * @param {JoinMarksIdentity} identity - Role, join source and call.
 * @returns {void}
 */
export function beginJoinMarks(identity) {
    if (session) {
        return;
    }
    session = {
        identity,
        libTimes: {
            conference: {},
            xmpp: {}
        },
        libTimesClosed: false,
        libTimesSettling: false,
        marks: {},
        prestarted: false,
        remoteVideoP2PTrackIds: new Set(),
        remoteVideoTrackIds: new Set(),
        sent: false,
        stopWatching: []
    };
    markJoin('appBoot');
    session.stopWatching.push(watchRemoteFrames(), watchPagehide(), watchLongTasks());
}
/**
 * How this page was entered: prerendered or not, when it was shown, how far a
 * prerender had got, the handoff's outcome, the gate's violations and the
 * device's memory class.
 *
 * @returns {JoinEntryFields}
 */
export function readJoinEntry() {
    const entry = getRoomEntry();
    let navigation;
    try {
        const perf = globalThis.performance;
        const entries = perf?.getEntriesByType?.('navigation');
        navigation = Array.isArray(entries) ? entries[0] : undefined;
    }
    catch {
        navigation = undefined;
    }
    const deviceMemory = globalThis.navigator?.deviceMemory;
    const preActivationStage = describePreActivationStage(entry.activationStart, navigation);
    const fields = {
        activationStart: Math.round(entry.activationStart),
        entryMode: entry.entryMode,
        gateViolations: entry.gateViolations,
        handoff: entry.handoff
    };
    // Only what is known: a page that was never prerendered has no stage.
    if (typeof deviceMemory === 'number' && Number.isFinite(deviceMemory)) {
        fields.deviceMemory = deviceMemory;
    }
    if (entry.handoffReason) {
        fields.handoffReason = entry.handoffReason;
    }
    if (preActivationStage) {
        fields.preActivationStage = preActivationStage;
    }
    return fields;
}
/**
 * Sends the marks, once per page load.
 *
 * @param {JoinMarksReason} reason - The first remote frame, or leaving first.
 * @returns {void}
 */
export function sendJoinMarks(reason) {
    if (!session || session.sent) {
        return;
    }
    const current = session;
    captureJoinLibTimes();
    current.sent = true;
    current.stopWatching.forEach(stop => {
        try {
            stop();
        }
        catch { /* nothing to undo */ }
    });
    current.stopWatching = [];
    try {
        const { identity } = current;
        const partial = reason !== 'firstRemoteFrame';
        const entry = readJoinEntry();
        const p2pDecision = getActiveTransport(current.conference)?.decision;
        const p2p = p2pDecision && (p2pDecision.enabled ? 'on' : p2pDecision.reason);
        // The XMPP login splits into the network (about 9 round trips) and
        // the main thread: its long tasks inside the login window.
        const xmppLongTaskMs = current.longTasks && longTaskOverlapMs(current.longTasks, current.libTimes.xmpp.connecting, current.libTimes.xmpp.connected);
        const networkRttMs = readNetworkRtt();
        emitMobileTelemetryEvent(JOIN_MARKS_EVENT, {
            ...entry,
            callId: identity.callId,
            marks: buildJoinMarks({
                activationStart: entry.activationStart,
                bundle: readBundleTiming(),
                conferenceTimes: current.libTimes.conference,
                graceOutcome: current.graceOutcome,
                marks: { ...readBrowserLoadMarks(), ...current.marks },
                xmppTimes: current.libTimes.xmpp
            }),
            // Which transport carried the first frame, and 'on' or why this
            // room stays on the bridge (p2pPolicy.ts), when known.
            ...current.firstFrameTransport ? { firstFrameTransport: current.firstFrameTransport } : {},
            ...p2p ? { p2p } : {},
            ...xmppLongTaskMs === undefined ? {} : { xmppLongTaskMs },
            ...networkRttMs === undefined ? {} : { networkRttMs },
            partial,
            prestarted: current.prestarted,
            reason,
            role: identity.role || undefined,
            source: identity.source || 'unknown'
        }, {
            callId: identity.callId,
            // Leaving is often the page unloading (beforeunload leaves the
            // conference, then pagehide): the request must outlive the page.
            keepalive: true,
            requestId: identity.requestId,
            roomName: identity.roomName,
            state: partial ? 'partial' : 'first_frame'
        });
    }
    catch { /* diagnostics must never break the room */ }
}
/**
 * The marks collected so far (tests and debugging).
 *
 * @returns {JoinMarks|undefined}
 */
export function peekJoinMarks() {
    return session ? {
        ...session.marks,
        graceOutcome: session.graceOutcome,
        prestarted: session.prestarted,
        sent: session.sent
    } : undefined;
}
/**
 * Forgets this page's marks (tests).
 *
 * @returns {void}
 */
export function resetJoinMarks() {
    session?.stopWatching.forEach(stop => stop());
    session = undefined;
}
